import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import Papa from 'papaparse';

const ExcelPreview = ({ filePath }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(null);

  const isCSV = filePath && filePath.toLowerCase().endsWith('.csv');

  useEffect(() => {
    const loadExcel = async () => {
      try {
        if (isCSV) {
          // Direct CSV parsing for CSV files
          const response = await fetch(filePath);
          if (!response.ok) throw new Error('Failed to fetch CSV file');

          const text = await response.text();
          Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              // Format data to match the same structure we use for Excel
              const headers = results.meta.fields || [];
              const rows = results.data.map(row =>
                headers.map(header => row[header] !== undefined ? row[header] : '')
              );

              setData({
                sheets: [{
                  name: 'Sheet 1',
                  headers,
                  rows
                }]
              });
              setLoading(false);
            },
            error: (error) => {
              console.error('Error parsing CSV:', error);
              setError('Error parsing CSV file');
              setLoading(false);
            }
          });
        } else {
          // Use API for Excel files
          const response = await fetch(`/api/convert/xlsx-to-json?file=${encodeURIComponent(filePath)}`);
          if (!response.ok) throw new Error('Failed to convert Excel file');

          const jsonData = await response.json();
          setData(jsonData);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading file:', error);
        setError(`Error loading ${isCSV ? 'CSV' : 'Excel'} file: ${error.message}`);
        setLoading(false);
      }
    };

    loadExcel();
  }, [filePath, isCSV]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary" />
          {/* <div className="mt-2">Loading {isCSV ? 'CSV' : 'Excel'} file...</div> */}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        <p className="mb-0">{error}</p>
        <p className="mt-2 mb-0">
          <small>Please try downloading the file to view it locally.</small>
        </p>
      </div>
    );
  }

  if (!data || !data.sheets || data.sheets.length === 0) {
    return (
      <div className="text-center p-4">
        <p>No data available or empty file</p>
      </div>
    );
  }

  // Handle direct CSV content display if needed
  if (isCSV && !data.sheets[0].headers.length) {
    return (
      <div className="csv-raw-content p-3">
        <pre style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {data.rawContent}
        </pre>
      </div>
    );
  }

  return (
    <div className="excel-preview p-3">
      {/* Sheet Tabs - Only show for Excel files with multiple sheets */}
      {!isCSV && data.sheets.length > 1 && (
        <div className="sheet-tabs mb-3 border-bottom">
          {data.sheets.map((sheet, index) => (
            <button
              key={index}
              className={`btn ${activeSheet === index ? 'btn-primary' : 'btn-outline-primary'} me-2 mb-2`}
              onClick={() => setActiveSheet(index)}
            >
              {sheet.name || `Sheet ${index + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Table Content */}
      <div className="table-responsive" style={{ maxHeight: '60vh' }}>
        <table className="table table-bordered table-hover table-striped">
          <thead className="table-light">
            <tr>
              {data.sheets[activeSheet].headers.map((header, index) => (
                <th key={index} className="px-3 py-2">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.sheets[activeSheet].rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-2">
                    {cell !== null && cell !== undefined ? cell : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* File Info */}
      <div className="mt-3 text-muted">
        <small>
          {isCSV
            ? `CSV file: ${data.sheets[activeSheet].rows.length} rows`
            : `Showing sheet ${activeSheet + 1} of ${data.sheets.length} | ${data.sheets[activeSheet].rows.length} rows`
          }
        </small>
      </div>
    </div>
  );
};

export default ExcelPreview; 